import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { IconButton, Popover } from '@mui/material';

import { AppDispatch, resetStore, RootState } from '../../redux/store';
import { searchUsers } from '../../redux/slices/someUserSlice';
import useWindowDimensions from '../../hooks/widthHook';
import { useNavigateToTop } from '../helpers/useNavigateToTop';

import { BurgerMenu } from '../BurgerMenu/BurgerMenu';
import { MobileSearch } from '../MobileSearch/MobileSearch';
import ModalWrapper from '../ModalConstructor/ModalWrapper/ModalWrapper';
import ModalContainer from '../ModalConstructor/ModalContainer/ModalContainer';
import ModalHeader from '../ModalConstructor/ModalHeader/ModalHeader';
import ModalFooter from '../ModalConstructor/ModalFooter/ModalFooter';
import SecondaryButton from '../shared/Buttons/SecondaryButton/SecondaryButton';
import MainButton from '../shared/Buttons/MainButton/MainButton';
import SearchInput from '../shared/SearchInput/SearchInput';

import InsertPhotoRoundedIcon from '@mui/icons-material/InsertPhotoRounded';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import NotificationsIcon from '@mui/icons-material/Notifications';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import GroupSharpIcon from '@mui/icons-material/GroupSharp';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import MenuIcon from '@mui/icons-material/Menu';
import HelpIcon from '@mui/icons-material/Help';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { useSocket } from '../../App';
import wizworkLogoText from '../../images/logo-mobile-version.png';
import wizworkLogo from '../../images/WizWorkLogo.png';
import './Header.scss';
import { GoToPrevPage } from '../GoToPrevPage/GoToPrevPage';
import { readNotifications } from '../../api/user';
import { setNotificationCounter } from '../../redux/slices/appSlice';
import { cleanAllChatVaues, cleanMsgs } from '../../redux/slices/chatSlice';

const Header = () => {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch<AppDispatch>();
  const [searchParams] = useSearchParams() || '';
  const chatId = searchParams.get('chatId') || '';

  const profile = useSelector((state: RootState) => state.profile);
  const { activity } = useSelector((state: RootState) => state.posts);
  const { msgCount } = useSelector((state: RootState) => state.chat);
  const { notificationsCounter } = useSelector((state: RootState) => state.app);
  const socket = useSocket();

  const {
    firstName,
    lastName,
    profileAvatar,
    organizationName,
    isAdmin,
  } = useSelector((state: RootState) => state.profile);
  const {
    searchedInterests,
    searchedCountry,
  } = useSelector((state: RootState) => state.someUser);
  const lan = localStorage.getItem('lang') || 'en';

  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const [showBurger, setShowBurger] = useState(false);

  let location = useLocation();
  const width = useWindowDimensions().width;
  const navigateToTop = useNavigateToTop();
  const navigate = useNavigate();

  const [searchValue, setSearchValue] = useState('');

  useEffect(() => {
    if (location.pathname === '/activity-log') {
      readNotifications()
      dispatch(setNotificationCounter(0))
    }
  }, [dispatch, activity?.length])

  useEffect(() => {
    if (searchValue.length) {
      dispatch(
        searchUsers({
          part: searchValue,
          interests: searchedInterests,
          country: searchedCountry,
        }),
      );
      if (isAdmin) {
        navigateToTop('/search-users-page');
      } else {
        navigateToTop('/search');
      }
      setSearchValue('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue, searchedInterests, searchedCountry]);

  // ========Popover============
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const open = Boolean(anchorEl);
  const id = open ? 'profile-popover' : undefined;

  return (
    <>
      <BurgerMenu showBurger={showBurger} setShowBurger={setShowBurger} />

      <header className="header-wrapper">
        <div className="header">
          <MobileSearch
            showMobileSearch={showMobileSearch}
            setShowMobileSearch={setShowMobileSearch}
          />

          <div className="header-box">
            {!isAdmin && <GoToPrevPage />}

            <div
              className="header-burger-button"
              onClick={() => {
                setShowBurger(!showBurger);
              }}
            >
              <IconButton>
                <div
                  className={`header-burger-button-img${showBurger ? ' header-burger-button-img--hide' : ''
                    }`}
                >
                  <MenuIcon />
                </div>

                <div
                  className={`header-burger-button-img-close${showBurger ? ' header-burger-button-img-close--show' : ''
                    }`}
                >
                  <CloseIcon color="secondary" />
                </div>
              </IconButton>
            </div>

            <a onClick={() => navigate('/home')}>
              <div className="header-logo-text-for-mobile">
                <img src={wizworkLogoText} alt="logo" width={102} height={20} />
              </div>
            </a>

            <a onClick={() => navigate('/home')}>
              <div className="header-logo">
                <img src={wizworkLogo} alt="logo" />
              </div>
            </a>

            <div className="header-input-container">
              <SearchInput setSearchValue={setSearchValue} />
            </div>
          </div>

          <ul className={isAdmin ? 'header-navigation header-navigation__admin' : 'header-navigation'}>


            <li
              className={`header-navigation__item${location.pathname === '/home'
                ? ' header-navigation__item--checked'
                : ''}`}
            >
              <a onClick={() => navigate('/home')}>
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12.2604 4.74745C12.1906 4.68069 12.0978 4.64343 12.0012 4.64343C11.9046 4.64343 11.8118 4.68069 11.742 4.74745L3.1123 12.9914C3.07566 13.0264 3.0465 13.0685 3.0266 13.1152C3.0067 13.1618 2.99647 13.212 2.99652 13.2628L2.99512 21C2.99512 21.3978 3.15315 21.7793 3.43446 22.0606C3.71576 22.3419 4.09729 22.5 4.49512 22.5H8.9998C9.19872 22.5 9.38948 22.4209 9.53013 22.2803C9.67079 22.1396 9.7498 21.9489 9.7498 21.75V15.375C9.7498 15.2755 9.78931 15.1801 9.85964 15.1098C9.92997 15.0395 10.0253 15 10.1248 15H13.8748C13.9743 15 14.0696 15.0395 14.14 15.1098C14.2103 15.1801 14.2498 15.2755 14.2498 15.375V21.75C14.2498 21.9489 14.3288 22.1396 14.4695 22.2803C14.6101 22.4209 14.8009 22.5 14.9998 22.5H19.5026C19.9004 22.5 20.282 22.3419 20.5633 22.0606C20.8446 21.7793 21.0026 21.3978 21.0026 21V13.2628C21.0027 13.212 20.9924 13.1618 20.9725 13.1152C20.9526 13.0685 20.9235 13.0264 20.8868 12.9914L12.2604 4.74745Z"
                    fill="#6C757D"
                  />
                  <path
                    d="M23.0114 11.4445L19.5052 8.09016V3C19.5052 2.80109 19.4262 2.61032 19.2855 2.46967C19.1449 2.32902 18.9541 2.25 18.7552 2.25H16.5052C16.3063 2.25 16.1155 2.32902 15.9748 2.46967C15.8342 2.61032 15.7552 2.80109 15.7552 3V4.5L13.0402 1.90406C12.7861 1.64719 12.4083 1.5 12 1.5C11.5931 1.5 11.2163 1.64719 10.9622 1.90453L0.991893 11.4436C0.70033 11.7248 0.663768 12.1875 0.92908 12.4922C0.995704 12.5691 1.07728 12.6316 1.16885 12.676C1.26041 12.7204 1.36005 12.7457 1.46169 12.7503C1.56334 12.755 1.66487 12.7389 1.7601 12.7031C1.85533 12.6672 1.94228 12.6124 2.01564 12.5419L11.7422 3.2475C11.812 3.18074 11.9048 3.14348 12.0014 3.14348C12.098 3.14348 12.1909 3.18074 12.2606 3.2475L21.9881 12.5419C22.1314 12.6793 22.3234 12.7543 22.5219 12.7504C22.7204 12.7465 22.9092 12.6641 23.047 12.5212C23.3349 12.2231 23.311 11.7309 23.0114 11.4445Z"
                    fill="#6C757D"
                  />
                </svg>
                <div>{formatMessage({ id: 'home' })}</div>
              </a>
            </li>


            <li
              className={`header-navigation__item${location.pathname === `/profile/${profile.id}`
                ? ' header-navigation__item--checked'
                : ''
                }`}
            >
              <a
                onClick={() => navigateToTop(`/profile/${profile.id}`)}
              >
                <AccountCircleIcon />
                <div>{formatMessage({ id: 'myProfile' })}</div>
              </a>
            </li>

            <li
              className={`header-navigation__item${location.pathname === `/chat`
                ? ' header-navigation__item--checked'
                : ''
                }`}
            >
              <a
                onClick={() => {
                  chatId ? navigate(`/chat?chatId=${chatId}`) : navigate(`/chat`);
                  dispatch(cleanMsgs());
                }}
              >
                <div className="header-navigation__mail-icon">
                  <MailOutlineIcon />
                  {!!msgCount?.length && <div className="header-navigation__msg-counter-wrapper">
                    <div className="header-navigation__msg-counter">
                      {msgCount?.length}
                    </div>
                  </div>}
                </div>
                <div>{formatMessage({ id: 'Chat' })}</div>
              </a>
            </li>

            {isAdmin && (
              <>
                <li
                  className={`header-navigation__item${location.pathname === `/profiles`
                    ? ' header-navigation__item--checked'
                    : ''
                    }`}
                >
                  <a
                    onClick={() => navigateToTop(`/profiles`)}
                  >
                    <GroupSharpIcon />
                    <div>Profiles</div>
                  </a>
                </li>

                <li
                  className={`header-navigation__item${location.pathname === `/posts`
                    ? ' header-navigation__item--checked'
                    : ''
                    }`}
                >
                  <a
                    onClick={() => navigateToTop(`/posts`)}
                  >
                    <InsertPhotoRoundedIcon />
                    <div>Posts</div>
                  </a>
                </li>

                <li
                  className={`header-navigation__item${location.pathname === `/reropted-comments`
                    ? ' header-navigation__item--checked'
                    : ''
                    }`}
                >
                  <a
                    onClick={() => navigateToTop(`/reropted-comments`)}
                  >
                    <ChatBubbleIcon />
                    <div>Comments</div>
                  </a>
                </li>
              </>
            )}

            {!isAdmin && (
              <li
                className={`header-navigation__item${location.pathname === '/activity-log'
                  ? ' header-navigation__item--checked'
                  : ''
                  }`}
              >
                <a
                  onClick={() => navigateToTop('/activity-log')}
                >
                  <NotificationsIcon />
                  <div>{formatMessage({ id: 'notifications' })} ({notificationsCounter})</div>
                </a>
              </li >
            )}
          </ul>

          <div className="header-search-icons-wrapper">
            {/* RELAX-ROOM for mobile */}
              {/*<Link*/}
              {/*  target="_blank"*/}
              {/*  rel="noopener noreferrer"*/}
              {/*  replace={false}*/}
              {/*  to="/relax-room"*/}
              {/*  onClick={() => dispatch(setOnRelaxRoom(true))}*/}
              {/*>*/}
              {/*  <div className="relax-room-mobile-btn">*/}
              {/*    <MainButton*/}
              {/*      text={formatMessage({ id: 'relaxRoom' })}*/}
              {/*      styles={{ width: 90, padding: '4px 4px', fontSize: 12 }}*/}
              {/*    />*/}
              {/*  </div>*/}
              {/*</Link>*/}

            <div
              className="header-search-icon"
              onClick={() => {
                setShowMobileSearch(true);
              }}
            >
              <SearchIcon
                style={{
                  color: '#6C757D',
                }}
              />
            </div>

            <div className="header-profile" onClick={handleClick}>
              <img src={profileAvatar} alt="" />

              <ArrowDropDownIcon
                className="header-arrow-icon"
                style={{
                  color: anchorEl ? '#791186' : '',
                  transform: anchorEl ? 'rotate(180deg)' : '',
                  transition: '0.3s',
                }}
              />
            </div>
          </div>



          <Popover
            id={id}
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            elevation={0}
            marginThreshold={72}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            PaperProps={{
              sx: {
                top: width < 1400 ? '57px !important' : '72px !important',
                left:
                  width >= 360 && width < 390
                    ? '28% !important'
                    : width >= 390 && width < 420
                      ? '32% !important'
                      : width >= 420 && width < 460
                        ? '38% !important'
                        : width >= 460 && width < 520
                          ? '43% !important'
                          : '',
              },
            }}
          >
            <div
              className="header-popover"
              style={{
                alignItems: lan === 'ar' ? 'flex-end' : 'flex-start',
              }}
            >
              <div className="header-popover__angle">
                <svg
                  width="15"
                  height="11"
                  viewBox="0 0 15 11"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M5.91198 0.699694C6.72167 -0.233232 8.2256 -0.233233 9.03529 0.699694L14.4736 6C15.5626 7.25474 14.657 11 12.9455 11L2.00178 11C0.290305 11 -0.615355 7.25474 0.473633 6L5.91198 0.699694Z"
                    fill="white"
                  />
                </svg>
              </div>

              <div
                className="header-popover__header"
                style={{
                  flexDirection: lan === 'ar' ? 'row-reverse' : 'row',
                }}
              >
                <img
                  src={profileAvatar}
                  alt=""
                  style={{
                    margin: lan === 'ar' ? '0 0 0 12px' : '0 12px 0 0',
                  }}
                />
                <div>
                  {organizationName}
                  {firstName} {lastName}
                </div>
              </div>

              <div className="header-popover__border"></div>

              <Link
                to="/account-settings"
                onClick={() => {
                  navigateToTop('/account-settings');
                  handleClose();
                }}
                style={{
                  width: '100%',
                }}
              >
                <div
                  className="header-popover__row"
                  style={{
                    flexDirection: lan === 'ar' ? 'row-reverse' : 'row',
                  }}
                >
                  <div
                    className="header-popover__icon"
                    style={{
                      margin: lan === 'ar' ? '0 0 0 8px' : '0 8px 0 0',
                    }}
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 18 18"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M7.66602 0.666626C6.60515 0.666626 5.58773 1.08805 4.83759 1.8382C4.08744 2.58834 3.66602 3.60576 3.66602 4.66663C3.66602 5.72749 4.08744 6.74491 4.83759 7.49505C5.58773 8.2452 6.60515 8.66663 7.66602 8.66663C8.72688 8.66663 9.7443 8.2452 10.4944 7.49505C11.2446 6.74491 11.666 5.72749 11.666 4.66663C11.666 3.60576 11.2446 2.58834 10.4944 1.8382C9.7443 1.08805 8.72688 0.666626 7.66602 0.666626ZM2.67502 9.66663C2.41165 9.66557 2.15067 9.71652 1.90704 9.81655C1.66341 9.91657 1.44192 10.0637 1.25527 10.2495C1.06863 10.4353 0.9205 10.6562 0.819382 10.8993C0.718264 11.1425 0.666145 11.4033 0.666016 11.6666C0.666016 13.3576 1.49902 14.6326 2.80102 15.4636C4.08302 16.2806 5.81102 16.6666 7.66602 16.6666C8.07602 16.6666 8.48202 16.6476 8.87602 16.6096C8.09094 15.634 7.66389 14.4189 7.66602 13.1666C7.66602 11.8366 8.13802 10.6166 8.92302 9.66663H2.67602H2.67502ZM9.30102 12.5866C9.55925 12.5206 9.80167 12.4036 10.014 12.2425C10.2264 12.0814 10.4044 11.8795 10.5377 11.6487C10.6709 11.4178 10.7566 11.1626 10.7898 10.8982C10.823 10.6337 10.803 10.3653 10.731 10.1086L10.576 9.55163C10.83 9.35663 11.105 9.18963 11.397 9.05463L11.735 9.41263C11.9219 9.61093 12.1474 9.76896 12.3975 9.877C12.6477 9.98504 12.9173 10.0408 13.1898 10.0409C13.4623 10.041 13.7319 9.98541 13.9822 9.87754C14.2324 9.76967 14.458 9.6118 14.645 9.41363L14.969 9.06963C15.267 9.20963 15.547 9.38463 15.804 9.58763L15.678 10.0106C15.6 10.2717 15.5761 10.5459 15.6079 10.8165C15.6397 11.087 15.7264 11.3483 15.8627 11.5842C15.9991 11.82 16.1821 12.0256 16.4008 12.1881C16.6194 12.3507 16.8689 12.4669 17.134 12.5296L17.483 12.6116C17.5231 12.9493 17.5265 13.2903 17.493 13.6286L17.033 13.7456C16.7746 13.8116 16.532 13.9285 16.3194 14.0896C16.1068 14.2507 15.9286 14.4527 15.7953 14.6837C15.662 14.9147 15.5762 15.17 15.543 15.4346C15.5098 15.6993 15.5299 15.9679 15.602 16.2246L15.758 16.7806C15.5033 16.9765 15.2276 17.1435 14.936 17.2786L14.598 16.9206C14.4112 16.7224 14.1859 16.5644 13.9358 16.4564C13.6858 16.3484 13.4163 16.2926 13.1100 16.2924C12.8716 16.2922 12.602 16.3476 12.3518 16.4553C12.1017 16.563 11.8761 16.7207 11.689 16.9186L11.364 17.2626C11.0669 17.1223 10.7867 16.9485 10.529 16.7446L10.656 16.3226C10.7342 16.0615 10.7582 15.7872 10.7265 15.5165C10.6948 15.2458 10.6081 14.9844 10.4718 14.7485C10.3354 14.5125 10.1523 14.3068 9.93358 14.1442C9.71489 13.9815 9.46526 13.8653 9.20002 13.8026L8.85002 13.7206C8.81008 13.3833 8.80673 13.0427 8.84002 12.7046L9.30102 12.5866ZM14.166 13.1666C14.166 12.9014 14.0607 12.6471 13.8731 12.4595C13.6856 12.272 13.4312 12.1666 13.166 12.1666C12.9008 12.1666 12.6464 12.272 12.4589 12.4595C12.2714 12.6471 12.166 12.9014 12.166 13.1666C12.166 13.4318 12.2714 13.6862 12.4589 13.8737C12.6464 14.0613 12.9008 14.1666 13.166 14.1666C13.4312 14.1666 13.6856 14.0613 13.8731 13.8737C14.0607 13.6862 14.166 13.4318 14.166 13.1666Z"
                        fill="#791186"
                      />
                    </svg>
                  </div>
                  <div>{formatMessage({ id: 'settingPrivacy' })}</div>
                </div>
              </Link>

              <div className="header-popover__border"></div>

              <Link
                to="/faq"
                onClick={() => {
                  navigateToTop('/faq');
                  handleClose();
                }}
                style={{
                  width: '100%',
                }}
              >
                <div
                  className="header-popover__row"
                  style={{
                    flexDirection: lan === 'ar' ? 'row-reverse' : 'row',
                  }}
                >
                  <div
                    className="header-popover__icon"
                    style={{
                      margin: lan === 'ar' ? '0 0 0 8px' : '0 8px 0 0',
                    }}
                  >
                    <HelpIcon style={{ width: 21, height: 21 }} />
                  </div>
                  <div>{formatMessage({ id: 'faq' })}</div>
                </div>
              </Link>

              <div className="header-popover__border"></div>

              <div
                className="header-popover__row header-popover__row--exit"
                style={{
                  flexDirection: lan === 'ar' ? 'row-reverse' : 'row',
                  width: '100%',
                }}
                onClick={() => {
                  handleClose();
                  setShowLogoutModal(true);
                  dispatch(cleanAllChatVaues());
                }}
              >
                <div
                  className="header-popover__icon"
                  style={{
                    margin: lan === 'ar' ? '0 0 0 8px' : '0 8px 0 0',
                  }}
                >
                  <ExitToAppIcon />
                </div>
                <div>{formatMessage({ id: 'logOut' })}</div>
              </div>
            </div>
          </Popover>
        </div>

        {showLogoutModal && (
          <ModalWrapper width={350}>
            <ModalContainer>
              <ModalHeader
                textHeader={formatMessage({ id: 'logOutConfirm' })}
                smallCross
                onClose={() => setShowLogoutModal(false)}
              />
              <ModalFooter>
                <SecondaryButton
                  text={formatMessage({ id: 'logOut' })}
                  styles={{ width: 143 }}
                  onClick={() => {
                    setShowLogoutModal(false);
                    handleClose();
                    resetStore();
                    if (socket) {
                      socket?.disconnect();
                    }
                  }}
                />
                <MainButton
                  text={formatMessage({ id: 'cancel' })}
                  onClick={() => {
                    setShowLogoutModal(false);
                  }}
                  styles={{ width: 143 }}
                />
              </ModalFooter>
            </ModalContainer>
          </ModalWrapper>
        )}
      </header>
    </>
  );
};

export default Header;
